/**
 * Server-Sent Events read from a fetch response (the viewer never uses EventSource: it cannot send
 * the ticket header, and a ticket in the URL would land in logs). Text in, `{event, data}` out; the
 * caller decides what each event means.
 */

export interface SseEvent {
  event: string;
  data: string;
}

/** Incremental parser: feed it text as it arrives, it hands back every complete event. */
export class SseParser {
  private buffer = '';

  /** A `\r` that ended the last chunk: its `\n` may start the next one (one CRLF, not two line ends). */
  private pendingCr = false;

  push(chunk: string): SseEvent[] {
    let text = this.pendingCr && chunk.startsWith('\n') ? chunk.slice(1) : chunk;
    this.pendingCr = text.endsWith('\r');
    text = text.replace(/\r\n?/g, '\n');
    this.buffer += text;
    const out: SseEvent[] = [];
    let end = this.buffer.indexOf('\n\n');
    while (end >= 0) {
      const event = parseBlock(this.buffer.slice(0, end));
      if (event) out.push(event);
      this.buffer = this.buffer.slice(end + 2);
      end = this.buffer.indexOf('\n\n');
    }
    return out;
  }

  /** The stream closed: a last event without its blank line still counts. */
  flush(): SseEvent[] {
    const rest = this.buffer;
    this.buffer = '';
    this.pendingCr = false;
    const event = rest.trim() ? parseBlock(rest) : null;
    return event ? [event] : [];
  }
}

function parseBlock(block: string): SseEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value || 'message';
    else if (field === 'data') data.push(value);
  }
  return data.length > 0 ? { event, data: data.join('\n') } : null;
}

/** JSON of an event's data, or null when it is not JSON. */
export function eventJson(event: SseEvent): unknown {
  try {
    return JSON.parse(event.data) as unknown;
  } catch {
    return null;
  }
}

/**
 * Reads `response` to the end, calling `onEvent` for each event as it arrives. `onEvent` answers
 * true to stop reading (a terminal event); the rest of the stream is then cancelled.
 */
export async function readSse(response: Response, onEvent: (event: SseEvent) => boolean | void): Promise<void> {
  const parser = new SseParser();
  const deliver = (events: SseEvent[]): boolean => events.some((e) => onEvent(e) === true);
  const reader = response.body?.getReader();
  if (!reader) {
    deliver([...parser.push(await response.text()), ...parser.flush()]);
    return;
  }
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (deliver(parser.push(decoder.decode(value, { stream: true })))) {
      await reader.cancel().catch(() => undefined);
      return;
    }
  }
  deliver([...parser.push(decoder.decode()), ...parser.flush()]);
}
